import React, { useState, useEffect } from 'react';
import './result.scss';
import { formatPrice } from '../../Utils/formatting';
import PurchaseService from '../../Data/PurchaseService';
import { Payment, Product, TossReceipt } from 'ai-api/types/purchase';
import { handleLinkClick } from '@/Utils/routing';

const PaymentSuccess: React.FC = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const orderid = queryParams.get('orderid');
    const productid = queryParams.get('productid');
    const paymentKey = queryParams.get('paymentKey');
    const amount = queryParams.get('amount');
    
    const [loading, setLoading] = useState(true);
    const [payment, setPayment] = useState<Payment | null>(null);
    const [product, setProduct] = useState<Product | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (productid) {
            const fetchProduct = async () => {
                try {
                    const productResponse = await PurchaseService.getProduct(productid);
                    const productData = productResponse.data || productResponse;
                    setProduct(productData);
                } catch (error) {
                    console.error('Failed to fetch product info:', error);
                }
            };
            fetchProduct();
        }
    }, [productid]);

    useEffect(() => {
        const validateTossPayment = async () => {
            try {
                const receipt: TossReceipt = {
                    paymentKey: paymentKey!,
                    orderId: orderid!,
                    amount: parseInt(amount!)
                };

                const response = await PurchaseService.validateTossPayment(orderid!, productid, receipt);

                if (response?.code === 200 && response?.data?.valid) {
                    await fetchPaymentInfo();
                } else {
                    setError('Payment validation failed');
                    setLoading(false);
                }
            } catch (error) {
                console.error('Failed to validate Toss payment:', error);
                setError('Failed to validate payment');
                setLoading(false);
            }
        };

        const fetchPaymentInfo = async () => {
            try {
                const response = await PurchaseService.getPayment(orderid!);

                if (response?.code === 200 && response?.data?.length > 0) {
                    const paymentData = response.data[0];
                    setPayment(paymentData);

                    if (paymentData.status !== 'succeeded') {
                        setError(`Payment status: ${paymentData.status}`);
                        setLoading(false);
                        return;
                    }

                    setLoading(false);
                } else {
                    setError('Payment not found');
                    setLoading(false);
                }
            } catch (error) {
                console.error('Failed to fetch payment info:', error);
                setError('Failed to load payment information');
                setLoading(false);
            }
        };

        const debounceTimer = setTimeout(() => {
            if (orderid && paymentKey && amount) {
                validateTossPayment();
            } else if (orderid) {
                fetchPaymentInfo();
            } else {
                setError('Invalid payment information');
                setLoading(false);
            }
        }, 200);

        return () => clearTimeout(debounceTimer);
    }, [orderid, paymentKey, amount, productid]);


    const getProductTypeDescription = (type: string, value: number) => {
        switch (type) {
            case 'subscription':
                const days = Math.floor(value / 86400);
                const hours = Math.floor((value % 86400) / 3600);
                const minutes = Math.floor((value % 3600) / 60);
                if (days > 0) return `${days} day${days > 1 ? 's' : ''} access`;
                if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} access`;
                return `${minutes} minute${minutes > 1 ? 's' : ''} session`;
            case 'coin':
                return `${value} coins`;
            case 'product':
                return 'Digital product';
            default:
                return 'Service';
        }
    };

    if (loading) {
        return (
            <div className="payment-result-loading">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p className="loading-text">{"Validating your payment..."}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="payment-result-container">
                <div className="payment-result-content">
                    <h2 className="title error">{"Payment Error"}</h2>
                    <div className="result-icon">
                        <svg viewBox="0 0 24 24" width="64" height="64">
                            <circle cx="12" cy="12" r="11" fill="#f44336" stroke="white" strokeWidth="2" />
                            <path d="M8 8l8 8M16 8l-8 8" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </div>
                    <p className="result-message">{error}</p>
                    <div className="section-action">
                        <a href="/" className="button secondary">{"Return to Home"}</a>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="payment-result-container">
            <div className="payment-result-content">
                <h2 className="title success">{"Payment Successful!"}</h2>
                
                <div className="result-icon">
                    <svg viewBox="0 0 24 24" width="64" height="64">
                        <circle cx="12" cy="12" r="11" fill="#4CAF50" stroke="white" strokeWidth="2" />
                        <path d="M7 13l3 3 7-7" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>

                <p className="result-message">
                    {payment?.type === 'subscription' 
                        ? "Your subscription has been activated successfully."
                        : payment?.type === 'coin'
                        ? "Your coins have been added to your account."
                        : "Your purchase has been completed successfully."}
                    {" We've sent a confirmation email with all the details."}
                </p>

                <div className="section">
                    <h2 className="section-title">{"Order Information"}</h2>
                    <div className="section-content">
                        {payment?.orderid && (
                            <div className="order-id">
                                {"Order ID: "}{payment.orderid}
                            </div>
                        )}
                    </div>
                </div>

                <div className="section">
                    <h2 className="section-title">{"Purchase Details"}</h2>
                    <div className="section-content">
                        {product && (
                            <>
                                <div className="summary-item">
                                    <span className="label">{"Product:"}</span>
                                    <span className="value">{product.name}</span>
                                </div>
                                {product.detail && (
                                    <div className="summary-item">
                                        <span className="label">{"Description:"}</span>
                                        <span className="value">{product.detail}</span>
                                    </div>
                                )}
                                <div className="summary-item">
                                    <span className="label">{"Type:"}</span>
                                    <span className="value">
                                        {getProductTypeDescription(product.type, product.value)}
                                    </span>
                                </div>
                            </>
                        )}
                        {payment && (
                            <>
                                {payment.quantity > 1 && (
                                    <div className="summary-item">
                                        <span className="label">{"Quantity:"}</span>
                                        <span className="value">{payment.quantity}</span>
                                    </div>
                                )}
                                <div className="summary-item">
                                    <span className="label">{"Total Paid:"}</span>
                                    <span className="value">
                                        {formatPrice(payment.finalAmount, payment.currency)}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="section">
                    <h2 className="section-title">{"What's Next?"}</h2>
                    <div className="section-content">
                        {payment?.type === 'subscription' ? (
                            <ul>
                                <li>{"Check your email for access details"}</li>
                                <li>{"Your subscription is now active"}</li>
                                <li>{"Access premium content from your dashboard"}</li>
                                {payment.remainder && (
                                    <li>{`Subscription valid for ${Math.floor(payment.remainder / 86400)} days`}</li>
                                )}
                            </ul>
                        ) : payment?.type === 'coin' ? (
                            <ul>
                                <li>{`${payment.value} coins have been added to your account`}</li>
                                <li>{"Use coins to unlock premium content"}</li>
                                <li>{"Check your balance in your profile"}</li>
                                <li>{"Coins never expire"}</li>
                            </ul>
                        ) : (
                            <ul>
                                <li>{"Wait for program details"}</li>
                                <li>{"Access your purchase from the "}<a href="/purchase/history" onClick={handleLinkClick}>{"history"}</a></li>
                                <li>{"Contact support if you have any questions"}</li>
                                <li>{"No worries for TOPIK from now!"}</li>
                            </ul>
                        )}
                    </div>
                </div>

                <div className="section-action">
                    <a href="/" className="button secondary">
                        {"Return to Home"}
                    </a>
                    {payment?.type === 'subscription' && (
                        <a href="/dashboard" className="button primary">
                            {"Go to Dashboard"}
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PaymentSuccess;